import { z } from 'zod';
import * as Sentry from '@sentry/browser';

/**
 * Creates a validator function for the given schema
 * @param {z.ZodType} schema - Zod schema to validate against
 * @param {string} contextName - Name of what's being validated
 * @returns {function} - Enhanced validator function
 */
export const createValidator = (schema, contextName) => {
  return (data, options = {}) => {
    const {
      actionName = 'unknown',
      location = 'unknown',
      direction = 'unknown',
      moduleFrom = 'unknown',
      moduleTo = 'unknown'
    } = options;

    try {
      return schema.parse(data);
    } catch (error) {
      const validationContext = {
        type: contextName,
        action: actionName,
        location,
        direction,
        flow: `${moduleFrom} → ${moduleTo}`,
        timestamp: new Date().toISOString()
      };
      
      const safeData = typeof data === 'object' ? 
        JSON.stringify(data, (key, value) => 
          ['password', 'token', 'secret'].includes(key) ? '[REDACTED]' : value
        ) : String(data);

      const formattedErrors = error.errors?.map(err => 
        `${err.path.join('.')}: ${err.message}`
      ).join('\n') || error.message;
      
      const errorMessage = `Validation failed in ${validationContext.action} (${validationContext.flow})\n` +
                          `Context: ${contextName} (${direction})\n` +
                          `Location: ${location}\n` +
                          `Errors:\n${formattedErrors}`;
      
      console.error(errorMessage, '\nReceived:', safeData);
      
      Sentry.captureException(error, {
        extra: {
          ...validationContext,
          receivedData: safeData,
          validationErrors: formattedErrors
        },
        tags: {
          validationType: contextName,
          validationAction: actionName,
          validationDirection: direction,
          moduleFlow: `${moduleFrom}-to-${moduleTo}`
        }
      });
      
      throw new Error(errorMessage);
    }
  };
};